import React from 'react';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

/**
 * Call-mode controls (#1) — an INLINE panel that takes over the text-input area
 * while a hands-free "call" is in progress. The transcript above stays visible
 * and words remain clickable. Rendered by the parent only while call mode is on.
 *
 * States (loopState): 'listening' (sends on a pause, or tap to finish), 'speaking'
 * (AI is talking — shows audio controls: play/pause + speed), 'thinking' (spinner),
 * 'paused' (mic off after no speech or an error — tap to talk again).
 *
 * Props:
 *   loopState, currentText, onTapDone, onResume, secondsLeft, onExit,
 *   isPlaying, onTogglePlay, playbackRate, onSetRate,
 *   variant — 'call' (conversations: "Call Mode") or 'story' (story sessions:
 *             "Hands Free mode"). Only the panel title differs.
 */
const STATUS_COPY = {
  speaking: 'Speaking…',
  listening: "Listening — I'll send when you pause, or tap the mic",
  thinking: 'Thinking…',
};
const COPY = {
  call: { title: 'CALL MODE', ...STATUS_COPY },
  story: { title: 'HANDS FREE MODE', ...STATUS_COPY },
};
const SPEEDS = [0.75, 0.9, 1, 1.1, 1.25];

export default function ConversationModeOverlay({
  loopState, currentText, onTapDone, onResume, secondsLeft = null, onExit,
  isPlaying, onTogglePlay, playbackRate = 1, onSetRate, variant = 'call',
}) {
  const copy = COPY[variant] || COPY.call;
  const isListening = loopState === 'listening';
  const isSpeaking = loopState === 'speaking';
  const isThinking = loopState === 'thinking';
  const isPaused = loopState === 'paused';

  const statusText = isSpeaking ? copy.speaking
    : isListening && secondsLeft != null ? `Sending in ${secondsLeft}s…`
    : isListening ? copy.listening
    : isThinking ? copy.thinking
    : isPaused ? 'Mic paused — tap to talk'
    : 'Getting ready…';

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', p: 2, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>{copy.title}</Typography>
        <Button size="small" startIcon={<CloseIcon />} onClick={onExit} sx={{ textTransform: 'none' }}>
          Exit
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
        {isListening && (
          <Box
            role="button"
            aria-label="I'm done speaking"
            onClick={onTapDone}
            sx={{
              width: 84, height: 84, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '3px solid', borderColor: 'error.main', color: 'error.main',
              animation: 'cmListen 1.4s ease-in-out infinite',
              '@keyframes cmListen': {
                '0%,100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.5)' },
                '50%': { boxShadow: '0 0 0 16px rgba(239,68,68,0)' },
              },
            }}
          >
            <MicIcon sx={{ fontSize: 42 }} />
          </Box>
        )}

        {isPaused && (
          <Box
            role="button"
            aria-label="Tap to talk"
            onClick={onResume}
            sx={{
              width: 84, height: 84, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '3px solid', borderColor: 'divider', color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            <MicIcon sx={{ fontSize: 42 }} />
          </Box>
        )}

        {isThinking && <CircularProgress size={40} />}

        {isSpeaking && (
          <>
            {currentText && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 560 }}>
                {currentText}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={onTogglePlay} color="primary" aria-label={isPlaying ? 'Pause' : 'Play'}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {SPEEDS.map((s) => (
                  <Button
                    key={s}
                    size="small"
                    variant={playbackRate === s ? 'contained' : 'text'}
                    onClick={() => onSetRate && onSetRate(s)}
                    sx={{ minWidth: 44, textTransform: 'none', borderRadius: 4, px: 1 }}
                  >
                    {s}x
                  </Button>
                ))}
              </Box>
            </Box>
          </>
        )}

        <Typography variant="body2" sx={{ fontWeight: 600 }}>{statusText}</Typography>
      </Box>
    </Box>
  );
}
